import type { ReactNode } from 'react'
import type { LocationSearchParams } from '../../hooks/useLocatiomSearchParams'
import { SearchFiltersContext } from './SearchFiltersContext'
import { cn } from '@/lib/utils'
import { RegionFilterMobile, RegionFilterDesktop, RegionSelectionMobile } from './RegionFilter'
import { DateFilterMobile, DateFilterDesktop } from './DateFilter'
import { BedsFilterMobile, BedsFilterDesktop } from './BedsFilter'
import { ConditionsFilterMobile, ConditionsFilterDesktop } from './ConditionsFilter'
import { LabelFilterMobile, LabelSelectionMobile, LabelFilterDesktop } from './LabelFilter'
import { FacilityFilterMobile, FacilitySelectionMobile, FacilityFilterDesktop } from './FacilityFilter'
import { CategoryFilterMobile, CategoryFilterDesktop } from './CategoryFilter'
import { SearchButtonMobile, SearchButtonDesktop } from './SearchButton'
import type { MobileFilterView } from './MobileSearchSummary'

interface SearchFiltersProviderProps {
  localFilters: LocationSearchParams
  filters: LocationSearchParams
  updateLocalFilters: (updates: Partial<LocationSearchParams>, commit?: boolean) => void
  commitFilters: () => void
  children: ReactNode
}

function SearchFiltersProvider({
  localFilters,
  filters,
  updateLocalFilters,
  commitFilters,
  children,
}: SearchFiltersProviderProps) {
  return (
    <SearchFiltersContext.Provider
      value={{
        localFilters,
        filters,
        updateLocalFilters,
        commitFilters,
      }}
    >
      {children}
    </SearchFiltersContext.Provider>
  )
}

interface SearchFiltersFormProps {
  localFilters: LocationSearchParams
  filters: LocationSearchParams
  updateLocalFilters: (updates: Partial<LocationSearchParams>, commit?: boolean) => void
  commitFilters: () => void
  showSearchButton?: boolean
}

interface SearchFiltersFormMobileProps extends SearchFiltersFormProps {
  view: MobileFilterView
  onViewChange: (view: MobileFilterView) => void
}

export function SearchFiltersFormMobile({
  localFilters,
  filters,
  updateLocalFilters,
  commitFilters,
  showSearchButton = true,
  view,
  onViewChange,
}: SearchFiltersFormMobileProps) {
  return (
    <SearchFiltersProvider
      localFilters={localFilters}
      filters={filters}
      updateLocalFilters={updateLocalFilters}
      commitFilters={commitFilters}
    >
      <div
        className={cn(
          'transition-opacity duration-200',
          view === 'filters' ? 'opacity-100' : 'pointer-events-none absolute opacity-0',
        )}
      >
        <div className="flex flex-col gap-3">
          <CategoryFilterMobile />
          <div className="flex flex-col gap-1">
            <RegionFilterMobile onOpenRegionSelection={() => onViewChange('region')} />
            <LabelFilterMobile onOpenLabelSelection={() => onViewChange('label')} />
            <FacilityFilterMobile onOpenFacilitySelection={() => onViewChange('facility')} />
          </div>
          <DateFilterMobile />
          <BedsFilterMobile />
          <ConditionsFilterMobile />
          {/* Pinned to the bottom of the scrolling sheet: with the type tiles
              the form outgrows a phone screen and the button must stay in view. */}
          {showSearchButton && (
            <div className="sticky bottom-0 -mx-4 bg-white px-4 pt-1 pb-4">
              <SearchButtonMobile />
            </div>
          )}
        </div>
      </div>
      <div
        className={cn(
          'pb-4 transition-opacity duration-200',
          view === 'region' ? 'opacity-100' : 'pointer-events-none absolute opacity-0',
        )}
      >
        <RegionSelectionMobile onSelect={() => onViewChange('filters')} />
      </div>
      <div
        className={cn(
          'pb-4 transition-opacity duration-200',
          view === 'label' ? 'opacity-100' : 'pointer-events-none absolute opacity-0',
        )}
      >
        <LabelSelectionMobile onSelect={() => onViewChange('filters')} />
      </div>
      <div
        className={cn(
          'pb-4 transition-opacity duration-200',
          view === 'facility' ? 'opacity-100' : 'pointer-events-none absolute opacity-0',
        )}
      >
        <FacilitySelectionMobile onDone={() => onViewChange('filters')} />
      </div>
    </SearchFiltersProvider>
  )
}

export function SearchFiltersFormDesktop({
  localFilters,
  filters,
  updateLocalFilters,
  commitFilters,
  showSearchButton = true,
}: SearchFiltersFormProps) {
  return (
    <SearchFiltersProvider
      localFilters={localFilters}
      filters={filters}
      updateLocalFilters={updateLocalFilters}
      commitFilters={commitFilters}
    >
      <div className="flex flex-col gap-5">
        <div className="flex flex-row items-end gap-6">
          <RegionFilterDesktop />
          <LabelFilterDesktop />
          <DateFilterDesktop />
          <BedsFilterDesktop />
          <FacilityFilterDesktop />
          <ConditionsFilterDesktop />
          {showSearchButton && <SearchButtonDesktop />}
        </div>
        <CategoryFilterDesktop />
      </div>
    </SearchFiltersProvider>
  )
}
